import { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { v4 as uuid } from 'uuid'
import { ItemId, TreeData } from '@atlaskit/tree'

import { getDataFromState } from '@components/CommandParameters/helpers'

import {
  useCreateContextMenuMutation,
  useFetchContextMenuByIdQuery,
  useUpdateContextMenuMutation,
} from '@redux/api/contextMenu.api'

import { useCommandSources, useDraggableTree, usePrompt, useYupValidationResolver } from '@hooks'
import { getTree, transformExpandedTreeData } from '@helpers'
import {
  CONTEXT_MENU_APPLICABLE_TO,
  CONTEXT_MENU_TABS,
  ELEMENT_TYPE_CONTEXT_MENU,
  MENU_POINT_TYPES,
  OPERATION_TYPE,
  ROUTES,
} from '@constants'
import {
  AutocompleteOption,
  ContextMenuUpdatedElement,
  TreeItemMenuPointOptionType,
  UpdatedElementMenuPointType,
  UpdatedElementMenuPointTypePayload,
  UpdatedElementVariableType,
  UpdatedVariableType,
} from '@types'

import { contextMenuSchema } from '@validations'

import { AddMenuPointForm } from '../components/AddMenuPointDialog'
import { VariableType } from '../ContextMenuCreateOrEdit'
import { buildMenuPointRequestData, getMenuPointOrderFromTree } from '../helpers'

export type ConfigContextMenuForm = {
  title: string
  object: AutocompleteOption<string> | null
  applicableTo: AutocompleteOption<CONTEXT_MENU_APPLICABLE_TO> | null
  variables: VariableType[]
}

export const useHandlers = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isCreate = ROUTES.CONFIG_CONTEXT_MENU_CREATE === location.pathname
  const isEdit = Boolean(id)

  const [skipDirty, setSkipDirty] = useState(false)
  const [initialTree, setInitialTree] = useState(getTree([]))
  const [currentTab, setCurrentTab] = useState(CONTEXT_MENU_TABS.MENU_POINT)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [updatedElements, setUpdatedElements] = useState<UpdatedElementMenuPointType[]>([])
  const [updatedElementsVariables, setUpdatedElementsVariables] = useState<
    UpdatedElementVariableType[]
  >([])
  const [editTreeItemForm, setEditTreeItemForm] = useState<TreeItemMenuPointOptionType | null>(null)
  const [treeParentItemId, setTreeParentItemId] = useState<ItemId | null>(null)
  const [configCommandInternalId, setConfigCommandInternalId] = useState<string | null>(null)

  const { objectsOptions, loadingObjects, systemsOptions, loadingSystems, objects } =
    useCommandSources()

  const { data: currentContextMenu } = useFetchContextMenuByIdQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  })

  const [createContextMenu, { isLoading: isLoadingCreate }] = useCreateContextMenuMutation()
  const [updateContextMenu, { isLoading: isLoadingUpdate }] = useUpdateContextMenuMutation()

  const resolver = useYupValidationResolver(contextMenuSchema)
  const methods = useForm<ConfigContextMenuForm>({
    defaultValues: {
      object: null,
      applicableTo: null,
      title: '',
      variables: [],
    },
    resolver,
    mode: 'all',
    reValidateMode: 'onChange',
  })
  const { fields, append, remove } = useFieldArray({
    name: 'variables',
    control: methods.control,
  })
  const {
    watch,
    handleSubmit,
    formState: { isDirty: isDirtyForm },
    reset,
  } = methods

  useEffect(() => {
    if (currentContextMenu && objects) {
      const { menuPoints, objectCode, ...data } = currentContextMenu
      const object = objects.find(({ code }) => code === objectCode)
      const applicableToValue = currentContextMenu?.applicableTo ?? null

      reset({
        ...data,
        object: object ? { id: object.code, label: object.title } : null,
        applicableTo: applicableToValue
          ? { id: applicableToValue, label: applicableToValue }
          : null,
      })

      const tree = getTree(menuPoints)
      setTree(tree)
      setInitialTree(tree)
    }
  }, [currentContextMenu, objects])

  const watchedObject = watch('object')
  const watchVariables = watch('variables')
  const watchApplicableTo = watch('applicableTo')

  const handleChangeTab = (_: SyntheticEvent<Element, Event>, value: number) => {
    setCurrentTab(value)
  }

  const handleCloseMenuPointDialog = () => {
    setShowAddDialog(false)
    setEditTreeItemForm(null)
    setTreeParentItemId(null)
  }

  const handleAddItem = () => {
    append({ field: '', pathStr: '', pathArray: [] })
  }

  const handleRemoveItem = (index: number) => {
    remove(index)
  }

  const handleSetUpdatedElement = (element: UpdatedElementMenuPointType) => {
    const findIndex = updatedElements.findIndex(el => el.treeItemId === element.treeItemId)

    if (findIndex !== -1) {
      const clone = cloneDeep(updatedElements)

      clone.splice(findIndex, 1, { ...clone[findIndex], ...element })
      setUpdatedElements(clone)

      return
    }

    setUpdatedElements(prev => [...prev, element])
  }

  const { tree, setTree, deleteTreeItem, addTreeItem } = useDraggableTree(initialTree)

  const handleDeleteTreeItem = (treeItemId: ItemId, itemId?: number) => {
    deleteTreeItem(treeItemId)
    if (itemId) {
      handleSetUpdatedElement({
        treeItemId,
        operationType: OPERATION_TYPE.DELETE,
        type: ELEMENT_TYPE_CONTEXT_MENU.POINT,
        data: { id: itemId },
        contextMenuId: currentContextMenu?.id,
      })
    } else {
      setUpdatedElements(updatedElements.filter(el => el.treeItemId !== treeItemId))
    }
  }

  const handleEditTreeItem = (treeItem: TreeItemMenuPointOptionType) => {
    setEditTreeItemForm(treeItem)
  }

  const handleClickAddTreeItem = (treeItemId: ItemId, itemId?: number) => {
    setTreeParentItemId(treeItemId)
    setShowAddDialog(true)
  }

  const handleClickAddMenuPoint = () => {
    setShowAddDialog(true)
  }

  const handleDragEnd = (treeItem: TreeItemMenuPointOptionType, _order: number) => {
    const { childrenMenu, treeItemParentId, ...itemData } = treeItem.data

    handleSetUpdatedElement({
      treeItemParentId,
      treeItemId: treeItem.id,
      data: itemData,
      operationType: treeItem.data.id ? OPERATION_TYPE.UPDATE : OPERATION_TYPE.CREATE,
      type: ELEMENT_TYPE_CONTEXT_MENU.POINT,
      contextMenuId: currentContextMenu?.id,
    })
  }

  const handleSaveMenuPoint = (data: AddMenuPointForm) => {
    const commandValue = data?.command

    if (editTreeItemForm && tree) {
      const newTree: TreeData = { ...tree }
      newTree.items[editTreeItemForm.id] = {
        ...editTreeItemForm,
        data: {
          ...editTreeItemForm.data,
          ...data,
          menuPointType: data.menuPointType?.id || null,
          objectType: data.objectType?.id || null,
          value:
            data.menuPointType?.id === MENU_POINT_TYPES.COMMAND && configCommandInternalId
              ? configCommandInternalId
              : data.value?.id,
          valueTitle: data.value?.label,
          actions: data.actions?.map(action => {
            return {
              ...action,
              viewPreFilterScript: action?.viewPreFilterScript ?? undefined,
            }
          }),
          commandParameters: getDataFromState(data.commandParameters).map(commandParameter => {
            return {
              ...commandParameter,
              propertyType: 'PARAMETER',
            }
          }),
          commandBody: getDataFromState(data.commandBody).map(commandBody => {
            return {
              ...commandBody,
              propertyType: 'BODY',
            }
          }),
          command:
            commandValue && commandValue?.sourceType?.id
              ? {
                  id: commandValue?.id ?? null,
                  name: commandValue?.name,
                  objectTitle: commandValue?.objectCode?.label,
                  httpMethod: commandValue?.httpMethod,
                  objectCode: commandValue?.objectCode?.id ?? null,
                  resource: commandValue?.sourceType?.id,
                }
              : null,
        },
      } as TreeItemMenuPointOptionType
      setTree(newTree)

      const { childrenMenu, treeItemParentId, ...itemData } =
        newTree.items[editTreeItemForm.id].data

      handleSetUpdatedElement({
        treeItemParentId,
        treeItemId: editTreeItemForm.id,
        data: itemData,
        operationType: itemData.id ? OPERATION_TYPE.UPDATE : OPERATION_TYPE.CREATE,
        type: ELEMENT_TYPE_CONTEXT_MENU.POINT,
        contextMenuId: currentContextMenu?.id,
      })

      handleCloseMenuPointDialog()

      return
    }

    const addedItem = addTreeItem(
      {
        uuid: uuid(),
        childrenMenu: [],
        ...data,
        menuPointType: data.menuPointType?.id || null,
        objectType: data.objectType?.id || null,
        value:
          data.menuPointType?.id === MENU_POINT_TYPES.COMMAND && configCommandInternalId
            ? configCommandInternalId
            : data.value?.id,
        ...(treeParentItemId &&
          tree?.items[treeParentItemId].data.id && {
            parentId: tree?.items[treeParentItemId].data.id,
          }),
        ...(treeParentItemId &&
          tree?.items[treeParentItemId].data.uuid && {
            parentUuid: tree?.items[treeParentItemId].data.uuid,
          }),
        valueTitle: data.value?.label,
        commandParameters: getDataFromState(data.commandParameters).map(commandParameter => {
          return {
            ...commandParameter,
            propertyType: 'PARAMETER',
          }
        }),
        commandBody: getDataFromState(data.commandBody).map(commandBody => {
          return {
            ...commandBody,
            propertyType: 'BODY',
          }
        }),
        command:
          commandValue && commandValue?.sourceType?.id
            ? {
                objectTitle: commandValue.objectCode?.label,
                id: commandValue?.id ?? null,
                name: commandValue?.name,
                httpMethod: commandValue?.httpMethod,
                objectCode: commandValue?.objectCode?.id ?? null,
                resource: commandValue?.sourceType?.id,
              }
            : null,
      },
      treeParentItemId ? tree?.items[treeParentItemId] : undefined
    )

    if (addedItem) {
      const { childrenMenu, treeItemParentId, ...itemData } = addedItem.data

      handleSetUpdatedElement({
        treeItemParentId,
        treeItemId: addedItem.id,
        data: itemData,
        operationType: OPERATION_TYPE.CREATE,
        type: ELEMENT_TYPE_CONTEXT_MENU.POINT,
        contextMenuId: currentContextMenu?.id,
      })
    }

    handleCloseMenuPointDialog()
  }

  const handleSaveContextMenu = handleSubmit((data: ConfigContextMenuForm) => {
    const updatedMenu: ContextMenuUpdatedElement | null =
      data.title !== currentContextMenu?.title ||
      data.applicableTo?.id !== currentContextMenu?.applicableTo
        ? {
            type: ELEMENT_TYPE_CONTEXT_MENU.CONTEXT,
            operationType: OPERATION_TYPE.UPDATE,
            contextMenuId: currentContextMenu?.id,
            data: {
              title: data.title,
              id: currentContextMenu?.id,
              objectCode: currentContextMenu?.objectCode,
              objectTitle: currentContextMenu?.objectTitle,
              applicableTo: data.applicableTo?.id ?? undefined,
            },
          }
        : null

    const menuPoints: UpdatedElementMenuPointTypePayload[] = updatedElements.map(
      // Убираются не нужные св-ва для бэка
      ({ treeItemId, treeItemParentId, data, ...el }) => {
        const contextMenuId = currentContextMenu ? currentContextMenu.id : null

        if (el.operationType === OPERATION_TYPE.DELETE) {
          return {
            ...el,
            contextMenuId,
            data,
          }
        }

        const order =
          tree && treeItemId
            ? getMenuPointOrderFromTree(tree, treeItemId, treeItemParentId)
            : 0

        if (data) {
          return {
            ...el,
            contextMenuId,
            data: buildMenuPointRequestData(
              data as Record<string, unknown>,
              order,
              tree,
              treeItemId
            ),
          }
        }

        return {
          ...el,
          contextMenuId,
        }
      }
    )

    if (!currentContextMenu) {
      const object = objects?.find(({ code }) => code === data.object?.id)
      const contextMenu: ContextMenuUpdatedElement = {
        type: ELEMENT_TYPE_CONTEXT_MENU.CONTEXT,
        operationType: OPERATION_TYPE.CREATE,
        contextMenuId: currentContextMenu?.id,
        data: {
          objectCode: object?.code,
          objectTitle: object?.title,
          title: data.title,
          applicableTo: data.applicableTo?.id ?? undefined,
        },
      }
      setSkipDirty(true)
      createContextMenu([contextMenu, ...menuPoints, ...updatedElementsVariables])
        .unwrap()
        .then(res => navigate(`${ROUTES.CONFIG_CONTEXT_MENU_EDIT}/${res.id}`))
        .finally(() => setSkipDirty(false))
    } else {
      const elements = [
        ...(updatedMenu ? [updatedMenu] : []),
        ...menuPoints,
        ...updatedElementsVariables,
      ]

      if (elements.length) {
        updateContextMenu({
          id: currentContextMenu.id,
          elements: [
            ...(updatedMenu ? [updatedMenu] : []),
            ...menuPoints,
            ...updatedElementsVariables,
          ],
        })
          .unwrap()
          .then(() => {
            setUpdatedElements([])
            setUpdatedElementsVariables([])
          })
      }
    }
  })

  const handleSetUpdatedElementVariables = (element: UpdatedElementVariableType) => {
    const findIndex = updatedElementsVariables.findIndex(
      el => el.data?.field === element.data?.field
    )

    if (findIndex !== -1) {
      const clone = cloneDeep(updatedElementsVariables)

      clone.splice(findIndex, 1, { ...clone[findIndex], ...element })
      setUpdatedElementsVariables(clone)

      return
    }

    setUpdatedElementsVariables(prev => [...prev, element])
  }

  const handleDeleteVariable = (variable: UpdatedVariableType) => {
    if (variable.id) {
      handleSetUpdatedElementVariables({
        operationType: OPERATION_TYPE.DELETE,
        type: ELEMENT_TYPE_CONTEXT_MENU.VARIABLE,
        data: currentContextMenu ? { ...variable, contextMenuId: currentContextMenu.id } : variable,
        contextMenuId: currentContextMenu?.id,
      })

      return
    }

    setUpdatedElementsVariables(
      updatedElementsVariables.filter(savedVar => savedVar.data?.field !== variable.field)
    )
  }

  const handleSaveVariable = (variable: UpdatedVariableType) => {
    handleSetUpdatedElementVariables({
      operationType: variable.id ? OPERATION_TYPE.UPDATE : OPERATION_TYPE.CREATE,
      type: ELEMENT_TYPE_CONTEXT_MENU.VARIABLE,
      data: currentContextMenu ? { ...variable, contextMenuId: currentContextMenu.id } : variable,
      contextMenuId: currentContextMenu?.id,
    })
  }

  const handleCancelContextMenu = () => {
    navigate(ROUTES.CONFIG_CONTEXT_MENU)
  }

  const handleSetConfigCommandInternalId = (value: string | null) => {
    setConfigCommandInternalId(value)
  }

  const transformFirstTree = useMemo(
    () => transformExpandedTreeData(getTree(currentContextMenu?.menuPoints || [])),
    [currentContextMenu]
  )
  const transformTree = useMemo(() => (tree ? transformExpandedTreeData(tree) : null), [tree])

  const isDirtyTree = useMemo(
    () => !isEqual(transformFirstTree, transformTree),
    [transformFirstTree, transformTree]
  )
  const globalDirty = useMemo(
    () => isDirtyTree || isDirtyForm || Boolean(updatedElementsVariables.length),
    [isDirtyTree, isDirtyForm, updatedElementsVariables]
  )

  const isLeaveEdit =
    globalDirty && !isLoadingCreate && !isCreate && !!currentContextMenu && !skipDirty
  const isLeaveCreate = globalDirty && isCreate && !isEdit && !isLoadingCreate && !skipDirty

  usePrompt({ when: isLeaveEdit || isLeaveCreate })

  return {
    data: {
      objects,
      currentContextMenu,
      isEdit,
      isLoadingContextMenu: isLoadingUpdate || isLoadingCreate,
    },
    state: {
      methods,
      currentTab,
      showAddDialog,
      updatedElements,
      editTreeItemForm,
      treeParentItemId,
      watchedObject,
      fields,
      watchVariables,
      tree,
      objectsOptions,
      systemsOptions,
      globalDirty,
      loadingObjects,
      loadingSystems,
      configCommandInternalId,
      watchApplicableTo,
    },
    handlers: {
      setTree,
      handleDeleteTreeItem,
      handleEditTreeItem,
      handleClickAddTreeItem,
      handleClickAddMenuPoint,
      handleDragEnd,
      handleSaveMenuPoint,
      handleSaveContextMenu,
      handleChangeTab,
      handleCloseMenuPointDialog,
      handleAddItem,
      handleRemoveItem,
      handleSetUpdatedElementVariables,
      handleDeleteVariable,
      handleSaveVariable,
      handleCancelContextMenu,
      handleSetConfigCommandInternalId,
    },
  }
}
